import CheckIcon from '@mui/icons-material/Check';
import LandscapeIcon from '@mui/icons-material/Landscape';
import { Box, Button, Chip, CircularProgress, Divider, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useThemeColors } from '../hooks/useThemeColors';
import { getVideoAnalysis } from '../services/api';
import { IMG } from '../theme/assets';
import type { TelopRating, TelopSpan, VideoAnalysisResult } from '../types';

interface Props {
  itemId: string;
  onSeek: (sec: number) => void;
}

const IN_PROGRESS = new Set([
  'pending',
  'preprocessing',
  'waiting_transcript',
  'analyzing',
  'aggregating',
]);

const RANK: Record<TelopRating, number> = { ok: 0, caution: 1, bad: 2 };

const fmt = (sec: number) => {
  const s = Math.floor(sec);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

export default function VideoAnalysisTab({ itemId, onSeek }: Props) {
  const t = useThemeColors();
  const [data, setData] = useState<VideoAnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [ratingFilter, setRatingFilter] = useState<Set<TelopRating>>(
    new Set(['ok', 'caution', 'bad']),
  );
  const [sceneOn, setSceneOn] = useState(true);
  const [showPersistent, setShowPersistent] = useState(false);
  const [screenOn, setScreenOn] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const RATING_META: Record<
    TelopRating,
    { mark: string; label: string; color: string; tint: string }
  > = {
    ok: {
      mark: '○',
      label: '問題なし',
      color: t.verdict.accurate.color,
      tint: t.verdict.accurate.tint,
    },
    caution: {
      mark: '△',
      label: '要確認',
      color: t.verdict.partial.color,
      tint: t.verdict.partial.tint,
    },
    bad: {
      mark: '×',
      label: '不整合',
      color: t.verdict.inaccurate.color,
      tint: t.verdict.inaccurate.tint,
    },
  };

  const fetchData = useCallback(async () => {
    const res = await getVideoAnalysis(itemId);
    if (res.success && res.data) {
      setData(res.data);
      if (IN_PROGRESS.has(res.data.analysisStatus ?? '')) {
        timer.current = setTimeout(fetchData, 5000);
      }
    }
    setLoading(false);
  }, [itemId]);

  useEffect(() => {
    fetchData();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [fetchData]);

  const telops: TelopSpan[] = useMemo(() => {
    const list = data?.telop_analysis?.transient_findings ?? [];
    return [...list].sort((a, b) => a.start_sec - b.start_sec);
  }, [data]);

  const counts = useMemo(() => {
    const c: Record<TelopRating, number> = { ok: 0, caution: 0, bad: 0 };
    for (const tl of telops) {
      const r = tl.check?.rating;
      if (r && r in c) c[r as TelopRating]++;
    }
    return c;
  }, [telops]);

  const scenes = useMemo(() => {
    const frames = data?.metadata?.frames ?? [];
    return frames
      .filter((f) => f.scene?.description)
      .map((f) => ({ sec: f.timecode_sec, desc: f.scene?.description as string }));
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const status = data?.analysisStatus;
  const inProg = IN_PROGRESS.has(status ?? '');
  const persistent: TelopSpan[] = data?.telop_analysis?.persistent_overlays ?? [];
  // 段0（program_map）の種別で分ける。開始時刻順に並べるとコーナーの進行がそのまま読める
  const byStart = (a: TelopSpan, b: TelopSpan) => a.start_sec - b.start_sec;
  const programOverlays = persistent.filter((p) => p.kind === 'program').sort(byStart);
  const segmentOverlays = persistent.filter((p) => p.kind === 'segment').sort(byStart);
  const otherOverlays = persistent
    .filter((p) => p.kind !== 'program' && p.kind !== 'segment')
    .sort(byStart);
  const screenTexts: TelopSpan[] = data?.telop_analysis?.screen_texts ?? [];

  const toggleRating = (r: TelopRating) =>
    setRatingFilter((prev) => {
      const n = new Set(prev);
      if (n.has(r)) n.delete(r);
      else n.add(r);
      return n;
    });

  // timecode(mm:ss) ごとに テロップ＋スタジオ表示＋情景 を1バケットに集約（同じ秒は同じカードに）
  type Bucket = {
    sec: number;
    key: string;
    telops: TelopSpan[];
    screens: string[];
    scenes: string[];
  };
  const bucketMap = new Map<string, Bucket>();
  const ensureBucket = (sec: number): Bucket => {
    const key = fmt(sec);
    let b = bucketMap.get(key);
    if (!b) {
      b = { sec, key, telops: [], screens: [], scenes: [] };
      bucketMap.set(key, b);
    }
    return b;
  };
  for (const tl of telops) {
    if (ratingFilter.has((tl.check?.rating ?? 'ok') as TelopRating)) {
      ensureBucket(tl.start_sec).telops.push(tl);
    }
  }
  if (screenOn) for (const s of screenTexts) ensureBucket(s.start_sec).screens.push(s.text);
  if (sceneOn) for (const s of scenes) ensureBucket(s.sec).scenes.push(s.desc);
  const buckets = [...bucketMap.values()]
    .filter((b) => b.telops.length > 0 || b.screens.length > 0 || b.scenes.length > 0)
    .sort((a, b) => a.sec - b.sec);

  return (
    <Box>
      {/* ===== ヘッダー（コンパクト） ===== */}
      <Box
        sx={{
          border: `1px solid ${t.line}`,
          borderRadius: 2,
          backgroundColor: t.paper,
          backdropFilter: 'blur(8px)',
          p: 1.5,
          mb: 2.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 900, color: t.ink }}>
            テロップ（時系列）
          </Typography>
          {inProg ? (
            <Chip
              size="small"
              icon={<CircularProgress size={12} sx={{ color: '#fff !important' }} />}
              label={`解析中 ${data?.analysisProgress ?? 0}%`}
              sx={{ height: 22, backgroundColor: '#3b82f6', color: '#fff', fontWeight: 700 }}
            />
          ) : status === 'completed' ? (
            <Chip
              size="small"
              icon={
                <CheckIcon sx={{ fontSize: 14, color: `${t.verdict.accurate.color} !important` }} />
              }
              label="完了"
              sx={{
                height: 22,
                fontWeight: 800,
                color: t.verdict.accurate.color,
                backgroundColor: t.verdict.accurate.tint,
                border: `1px solid ${t.verdict.accurate.color}55`,
              }}
            />
          ) : null}
          {status === 'completed' && (
            <Typography sx={{ ml: 'auto', fontSize: 12, color: t.muted }}>
              テロップ <b style={{ color: t.ink, fontSize: 14 }}>{telops.length}</b> 件
            </Typography>
          )}
          {(data?.cmRanges?.length ?? 0) > 0 && (
            <Chip
              size="small"
              label={`CM ${data?.cmRanges?.length}帯 ${fmt(data?.cmTotalSec ?? 0)} を除外`}
              title={data?.cmRanges
                ?.map((r) => `${fmt(r.start_sec)}–${fmt(r.end_sec)} ${r.label ?? ''}`)
                .join('\n')}
              sx={{ height: 22, color: t.muted, border: `1px solid ${t.line}` }}
              variant="outlined"
            />
          )}
        </Box>

        {status === 'skipped_unsupported' ? (
          <Typography sx={{ color: t.verdict.partial.color, fontSize: 12.5 }}>
            縦動画のため映像解析はスキップされました（v1では横動画のみ対応）。
          </Typography>
        ) : status === 'error' ? (
          <Typography sx={{ color: t.verdict.inaccurate.color, fontSize: 12.5 }}>
            映像解析でエラーが発生しました: {data?.analysisError}
          </Typography>
        ) : (
          <Typography sx={{ color: t.faint, fontSize: 11.5, lineHeight: 1.55, mb: 1.25 }}>
            情景・発話に照らした妥当性判定（補助）。最終確認は人が行ってください。
          </Typography>
        )}

        {status === 'completed' && (
          <Box sx={{ display: 'flex', gap: 0.875, flexWrap: 'wrap' }}>
            {(['ok', 'caution', 'bad'] as TelopRating[]).map((r) => {
              const on = ratingFilter.has(r);
              const m = RATING_META[r];
              return (
                <Box
                  key={r}
                  onClick={() => toggleRating(r)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.875,
                    px: 1.375,
                    py: 0.625,
                    borderRadius: 999,
                    cursor: 'pointer',
                    userSelect: 'none',
                    border: `1px solid ${on ? m.color : t.line}`,
                    backgroundColor: on ? m.tint : 'transparent',
                    opacity: on ? 1 : 0.45,
                    transition: 'all .15s',
                  }}
                >
                  <Box
                    sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: m.color }}
                  />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.ink }}>
                    {m.label}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 900, color: on ? m.color : t.muted }}>
                    {counts[r]}
                  </Typography>
                </Box>
              );
            })}
            {/* 情景の表示トグル（タイムラインに混在表示） */}
            {scenes.length > 0 && (
              <Box
                onClick={() => setSceneOn((v) => !v)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.875,
                  px: 1.375,
                  py: 0.625,
                  borderRadius: 999,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: `1px solid ${sceneOn ? t.teal : t.line}`,
                  backgroundColor: sceneOn ? `${t.teal}1f` : 'transparent',
                  opacity: sceneOn ? 1 : 0.45,
                  transition: 'all .15s',
                }}
              >
                <LandscapeIcon sx={{ fontSize: 15, color: t.teal }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.ink }}>情景</Typography>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 900, color: sceneOn ? t.teal : t.muted }}
                >
                  {scenes.length}
                </Typography>
              </Box>
            )}
            {/* スタジオ表示（モニター/フリップ・判定対象外）トグル */}
            {screenTexts.length > 0 && (
              <Box
                onClick={() => setScreenOn((v) => !v)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.875,
                  px: 1.375,
                  py: 0.625,
                  borderRadius: 999,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: `1px solid ${screenOn ? t.indigo : t.line}`,
                  backgroundColor: screenOn ? `${t.indigo}1f` : 'transparent',
                  opacity: screenOn ? 1 : 0.45,
                  transition: 'all .15s',
                }}
              >
                <Box sx={{ fontSize: 13 }}>🖥</Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.ink }}>
                  スタジオ表示
                </Typography>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 900, color: screenOn ? t.indigo : t.muted }}
                >
                  {screenTexts.length}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* ===== タイムライン（同じ秒のテロップ＋情景を1カードに集約） ===== */}
      {buckets.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            py: 5,
            color: t.faint,
          }}
        >
          <Box component="img" src={IMG.owl.clipboard} alt="" sx={{ width: 96, opacity: 0.85 }} />
          <Typography variant="body2">
            {inProg ? '解析中です…' : '表示できる項目がありません。'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', ml: '108px' }}>
          {/* 縦の時間軸ライン */}
          <Box
            sx={{
              position: 'absolute',
              left: '-32px',
              top: 6,
              bottom: 6,
              width: '2px',
              backgroundColor: t.line,
            }}
          />
          {buckets.map((b) => {
            // 情景のみ（同時刻にテロップ無し）＝ 点線のスタンドアロン情景カード
            if (b.telops.length === 0) {
              return (
                <Box key={b.key} sx={{ position: 'relative', mb: 2 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '-104px',
                      top: 8,
                      width: 44,
                      textAlign: 'right',
                      color: t.faint,
                      fontSize: 13,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {b.key}
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '-38px',
                      top: 10,
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      border: `2px solid ${t.faint}`,
                      backgroundColor: t.paper,
                    }}
                  />
                  <Box
                    onClick={() => onSeek(b.sec)}
                    sx={{
                      border: `1px dashed ${t.line}`,
                      borderRadius: 2,
                      px: 1.75,
                      py: 1.25,
                      cursor: 'pointer',
                    }}
                  >
                    {b.screens.length > 0 && (
                      <Box sx={{ mb: b.scenes.length > 0 ? 1 : 0 }}>
                        <Typography
                          sx={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: '.06em',
                            color: t.indigo,
                            mb: 0.25,
                          }}
                        >
                          🖥 スタジオ表示（判定対象外）
                        </Typography>
                        {b.screens.map((d, i) => (
                          <Typography
                            key={i}
                            sx={{
                              fontSize: 13.5,
                              lineHeight: 1.6,
                              color: t.muted,
                              mt: i === 0 ? 0 : 0.5,
                            }}
                          >
                            {d}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {b.scenes.length > 0 && (
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: '.06em',
                            color: t.faint,
                            mb: 0.25,
                          }}
                        >
                          情景
                        </Typography>
                        {b.scenes.map((d, i) => (
                          <Typography
                            key={i}
                            sx={{
                              fontSize: 13.5,
                              lineHeight: 1.6,
                              color: t.muted,
                              mt: i === 0 ? 0 : 0.5,
                            }}
                          >
                            {d}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }
            const g = b;
            const worst = g.telops.reduce<TelopRating>((a, tl) => {
              const r = (tl.check?.rating ?? 'ok') as TelopRating;
              return RANK[r] > RANK[a] ? r : a;
            }, 'ok');
            return (
              <Box key={g.key} sx={{ position: 'relative', mb: 2 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: '-104px',
                    top: 10,
                    width: 44,
                    textAlign: 'right',
                    color: t.muted,
                    fontSize: 13,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {g.key}
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    left: '-39px',
                    top: 11,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: `3px solid ${t.paper}`,
                    backgroundColor: RATING_META[worst].color,
                  }}
                />
                <Box
                  sx={{
                    border: `1px solid ${t.line}`,
                    borderRadius: 2,
                    backgroundColor: t.paper,
                    backdropFilter: 'blur(8px)',
                    overflow: 'hidden',
                  }}
                >
                  {/* カードヘッダー: タイムコード + 同時表示件数 */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.75,
                      py: 0.875,
                      borderBottom: `1px solid ${t.line}`,
                    }}
                  >
                    <Chip
                      label={`▶ ${g.key}`}
                      size="small"
                      onClick={() => onSeek(g.sec)}
                      variant="outlined"
                      sx={{ height: 22, cursor: 'pointer', color: t.teal, borderColor: t.teal }}
                    />
                    {g.telops.length + g.screens.length + g.scenes.length > 1 && (
                      <Typography sx={{ fontSize: 11.5, color: t.muted }}>
                        同時 {g.telops.length + g.screens.length + g.scenes.length}件
                      </Typography>
                    )}
                  </Box>
                  {/* 同時刻のテロップ＋情景 */}
                  <Box sx={{ px: 1.75 }}>
                    {g.telops.map((tl, i) => {
                      const r = (tl.check?.rating ?? 'ok') as TelopRating;
                      const m = RATING_META[r];
                      return (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex',
                            gap: 1.25,
                            alignItems: 'flex-start',
                            py: 1.375,
                            borderTop: i === 0 ? 'none' : `1px solid ${t.line}`,
                          }}
                        >
                          <Box
                            sx={{
                              fontWeight: 900,
                              fontSize: 14,
                              width: 16,
                              textAlign: 'center',
                              flexShrink: 0,
                              color: m.color,
                              lineHeight: 1.5,
                            }}
                          >
                            {m.mark}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, lineHeight: 1.5, color: t.ink }}>
                              {tl.text}
                            </Typography>
                            {tl.check?.reason && (
                              <Typography
                                sx={{ mt: 0.625, fontSize: 13, lineHeight: 1.6, color: m.color }}
                              >
                                {tl.check.reason}
                              </Typography>
                            )}
                            {tl.check?.suspected_typo?.suggestion && (
                              <Typography
                                sx={{ mt: 0.625, fontSize: 13, color: t.verdict.partial.color }}
                              >
                                誤字疑い: {tl.check.suspected_typo.text} →{' '}
                                {tl.check.suspected_typo.suggestion}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                    {/* 同時刻のスタジオ表示（判定対象外・グレー） */}
                    {g.screens.map((d, i) => (
                      <Box
                        key={`scr-${i}`}
                        sx={{
                          display: 'flex',
                          gap: 1.25,
                          alignItems: 'flex-start',
                          py: 1.375,
                          borderTop: `1px solid ${t.line}`,
                        }}
                      >
                        <Box sx={{ fontSize: 15, width: 16, flexShrink: 0, textAlign: 'center' }}>
                          🖥
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 800,
                              letterSpacing: '.06em',
                              color: t.indigo,
                              mb: 0.25,
                            }}
                          >
                            スタジオ表示（判定対象外）
                          </Typography>
                          <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: t.muted }}>
                            {d}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    {/* 同時刻の情景（テロップと同じカード内・グレー） */}
                    {g.scenes.map((d, i) => (
                      <Box
                        key={`sc-${i}`}
                        sx={{
                          display: 'flex',
                          gap: 1.25,
                          alignItems: 'flex-start',
                          py: 1.375,
                          borderTop: `1px solid ${t.line}`,
                        }}
                      >
                        <LandscapeIcon
                          sx={{ fontSize: 16, color: t.faint, width: 16, flexShrink: 0, mt: '2px' }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 800,
                              letterSpacing: '.06em',
                              color: t.faint,
                              mb: 0.25,
                            }}
                          >
                            情景
                          </Typography>
                          <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: t.muted }}>
                            {d}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* 常設オーバーレイ（折りたたみ・番組共通/コーナー別） */}
      {persistent.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1, borderColor: t.line }} />
          <Button size="small" onClick={() => setShowPersistent((v) => !v)} sx={{ color: t.muted }}>
            {showPersistent ? '▼' : '▶'} 常設テロップ（判定対象外 {persistent.length}）
            {programOverlays.length > 0 && ` 番組共通 ${programOverlays.length}`}
            {segmentOverlays.length > 0 && ` / コーナー ${segmentOverlays.length}`}
          </Button>
          {showPersistent && (
            <Box sx={{ pl: 0.5, pt: 0.5 }}>
              {(
                [
                  {
                    key: 'program',
                    label: '番組共通',
                    hint: '番組を通して出ているもの（局ロゴ・企画タイトル等）',
                    list: programOverlays,
                    color: t.indigo,
                  },
                  {
                    key: 'segment',
                    label: 'コーナー',
                    hint: 'そのコーナーの間だけ出ているもの（お題バナー・進行表示等）',
                    list: segmentOverlays,
                    color: t.teal,
                  },
                  {
                    key: 'other',
                    label: 'その他',
                    hint: '長く出続けていたため常設と判定したもの',
                    list: otherOverlays,
                    color: t.faint,
                  },
                ] as const
              )
                .filter((g) => g.list.length > 0)
                .map((g) => (
                  <Box key={g.key} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 900,
                          color: g.color,
                          letterSpacing: '.04em',
                        }}
                      >
                        {g.label} {g.list.length}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: t.faint }}>{g.hint}</Typography>
                    </Box>
                    {g.list.map((p, i) => (
                      <Box
                        key={`${g.key}-${i}`}
                        onClick={() => onSeek(p.start_sec)}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '92px 54px 1fr',
                          gap: 1,
                          alignItems: 'baseline',
                          py: 0.4,
                          px: 0.75,
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: `${g.color}14` },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: t.faint,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                          title={(p.occurrences ?? [])
                            .map((o) => `${fmt(o.start_sec)}–${fmt(o.end_sec)}`)
                            .join('\n')}
                        >
                          {(p.occurrences?.length ?? 1) > 1
                            ? `${fmt(p.occurrences?.[0].start_sec ?? p.start_sec)} ×${p.occurrences?.length}`
                            : `${fmt(p.start_sec)}–${fmt(p.end_sec)}`}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: t.faint }}>
                          {p.screen_position || '—'}
                        </Typography>
                        <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: t.muted }}>
                          {p.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
